import format from 'date-fns/format';
import { CurrentShow } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { showInfo } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow.utils';
import { CouponItemApplicableOrderTypeEnum } from '../../generated/api';
import * as ClientModels from '../../models/ClientModels';
import { CouponCreateMethod, CouponType } from '../../models/ClientModels';
import { assertNever } from '../../utils/typeHelper';
import { WithID } from '../firebase/rxfireExtensions';

export type UniversalCoupon =
  | UniversalCoupon.AmountCoupon
  | UniversalCoupon.PercentCoupon
  | UniversalCoupon.FreeShippingCoupon;

export namespace UniversalCoupon {
  // mainly copied from ClientModels/Coupon
  export interface CouponCommon {
    code: string;
    enabled: boolean;

    // quantityLimit is useless, so we removed it
    // quantityLimit: boolean

    quantity?: number | null;
    redeemedQuantity: number;
    createdAt: Date;
    startedAt: Date;
    expiredAt: Date;
    storeIds?: string[];

    // store is incompatible between ClientModels/Coupon and
    // ApiServer/CouponItem, so we keep their intersection
    // store?: StoreFront;
    store?: { name: string };

    userGroupIds?: string[];
    sellerGroupIds?: string[];
    onlyNewUser?: boolean;
    onlyPurchasedUser?: boolean;
    onlyFollower?: boolean;
    oncePerStore?: boolean;
    onlyNewUserPerStore?: boolean;
    onlyReactivatedUser?: boolean;

    // useless for frontend
    // followSelectedSellers?: boolean;

    couponCreateMethod?: CouponCreateMethod;
    liveStreamIds?: string[];

    // useless for frontend
    // visible?: boolean;

    // currently useless for frontend
    // createdBy?;

    // currently useless for frontend
    // lastUpdatedBy?

    /// properties from CouponItem
    name?: string;
    description?: string;
    imageUrl?: string;
    pagUrl?: string;
  }

  export interface PercentCoupon extends CouponCommon {
    type: CouponType.discountPercent;
    discountPercent: number; // 0-100
    maximumDiscount?: number;
    excludedDropshipPartnerIds?: number[];
    applicableOrderType: CouponItemApplicableOrderTypeEnum;
  }

  export interface AmountCoupon extends CouponCommon {
    type: CouponType.discountAmount;
    discountAmount: number; // 5 = $5.00
    minimalPurchase?: number;
  }

  export interface FreeShippingCoupon extends CouponCommon {
    type: CouponType.freeShipping;
    minimalPurchase?: number;
  }

  export const isNotStarted = (coupon: UniversalCoupon): boolean => {
    return coupon.startedAt.getTime() > Date.now();
  };

  export const isExpired = (coupon: UniversalCoupon): boolean => {
    return coupon.expiredAt.getTime() < Date.now();
  };

  export const isRelatedWithShow = (
    coupon: WithID<UniversalCoupon>,
    currentShow: CurrentShow,
  ): boolean => {
    if (coupon.liveStreamIds && coupon.liveStreamIds.length) {
      const liveStreamId = showInfo(currentShow).relatedLiveStreamId;
      if (!liveStreamId) return false;
      if (!coupon.liveStreamIds.includes(liveStreamId)) return false;
    }

    if (coupon.storeIds && coupon.storeIds.length) {
      const seller = CurrentShow.getSeller(currentShow);
      if (!seller) return false;
      if (!coupon.storeIds.includes(seller.id)) return false;
    }

    return true;
  };

  export type ClientModel =
    | ClientModels.AmountCoupon
    | ClientModels.FreeShippingCoupon;
  export function fromClientModel(
    coupon: WithID<ClientModel>,
  ): WithID<UniversalCoupon>;
  export function fromClientModel(coupon: ClientModel): UniversalCoupon;
  export function fromClientModel(coupon: ClientModel): UniversalCoupon {
    return {
      ...coupon,
      createdAt: coupon.createdAt.toDate(),
      startedAt: coupon.startedAt.toDate(),
      expiredAt: coupon.expiredAt.toDate(),
    };
  }
}

export type UniversalRedeemableCoupon =
  | UniversalRedeemableCoupon.Nonredeemable
  | UniversalRedeemableCoupon.Redeemable
  | UniversalRedeemableCoupon.Redeemed;
export namespace UniversalRedeemableCoupon {
  export enum Status {
    Nonredeemable = 'Nonredeemable',
    Redeemable = 'Redeemable',
    Redeemed = 'Redeemed',
  }

  export interface Common {
    couponId: string;
    coupon: UniversalCoupon;
  }

  export interface Nonredeemable extends Common {
    status: Status.Nonredeemable;
    reason: string;
  }

  export interface Redeemable extends Common {
    status: Status.Redeemable;
  }

  export interface Redeemed extends Common {
    status: Status.Redeemed;
    redeemedCouponId: string;
    usedAt?: Date;
  }
}

// mainly copied from ClientModels/RedeemedCoupon
export interface UniversalRedeemedCoupon {
  couponId: string;
  coupon: UniversalCoupon;
  usedAt?: Date;

  // blow properties not provided from ApiServer, so all removed
  // buyerId: string;
  // buyerProfile: Profile;
  // createdAt: Date;
  // usedAt?: Date;
  // used: boolean;
  // orderId?: string;
}
export namespace UniversalRedeemedCoupon {
  export type ClientModel<
    T extends UniversalCoupon.ClientModel = UniversalCoupon.ClientModel,
  > = Omit<ClientModels.RedeemedCoupon, 'coupon'> & {
    coupon: T;
  };

  export function fromClientModel(
    coupon: WithID<ClientModel>,
  ): WithID<UniversalRedeemedCoupon>;
  export function fromClientModel(coupon: ClientModel): UniversalRedeemedCoupon;
  export function fromClientModel(
    coupon: ClientModel,
  ): UniversalRedeemedCoupon {
    return {
      ...coupon,
      usedAt: coupon.usedAt?.toDate(),
      coupon: UniversalCoupon.fromClientModel(coupon.coupon),
    };
  }
}

export const DATE_FORMATTER = 'MM/dd/yy hh:mm aaa';

export function couponOfferContent(coupon: UniversalCoupon): string {
  return coupon.type === CouponType.discountAmount
    ? `$${coupon.discountAmount.toFixed(2)}`
    : coupon.type === CouponType.discountPercent
    ? `${coupon.discountPercent.toFixed(0)}% OFF` +
      (coupon.applicableOrderType ===
      CouponItemApplicableOrderTypeEnum.ChildOrder
        ? ' (S)'
        : '')
    : coupon.type === CouponType.freeShipping
    ? 'free shipping'
    : assertNever(coupon);
}

export function couponSummary(coupon: UniversalCoupon): string[] {
  const parts = [
    `This promo will expire on ${format(coupon.expiredAt, DATE_FORMATTER)}`,
  ];

  if (
    coupon.type === CouponType.discountAmount &&
    coupon.minimalPurchase != null
  ) {
    parts.push(`Minimum purchase: $${coupon.minimalPurchase.toFixed(2)}`);
  }

  if (coupon.quantity != null) {
    parts.push(
      `Promos available: ${coupon.redeemedQuantity}/${coupon.quantity}`,
    );
  }

  return parts;
}
